import { useState, useEffect } from 'react';

const FETCH_TIMEOUT_MS = 10000;

type ResourceStatus = 'idle' | 'loading' | 'success' | 'error' | 'timeout';

export function useSceneDetection(
  sceneDetectionUrl: string | null | undefined,
  inlineSceneDetection?: any,
) {
  const [sceneDetection, setSceneDetection] = useState<any>(inlineSceneDetection || null);
  const [loading, setLoading] = useState<boolean>(!!sceneDetectionUrl && !inlineSceneDetection);
  const [status, setStatus] = useState<ResourceStatus>(inlineSceneDetection ? 'success' : sceneDetectionUrl ? 'loading' : 'idle');
  const [error, setError] = useState<Error | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!sceneDetectionUrl) {
      setSceneDetection(inlineSceneDetection || null);
      setLoading(false);
      setStatus(inlineSceneDetection ? 'success' : 'idle');
      setError(null);
      setTimedOut(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort('timeout'), FETCH_TIMEOUT_MS);

    setLoading(true);
    setStatus('loading');
    setError(null);
    setTimedOut(false);

    let cancelled = false;
    const startedAt = performance.now();

    fetch(sceneDetectionUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Scene detection request failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setSceneDetection(data);
          setLoading(false);
          setStatus('success');
          console.info('[clips] scene detection loaded', {
            url: sceneDetectionUrl,
            durationMs: Math.round(performance.now() - startedAt),
          });
        }
      })
      .catch((err) => {
        console.error('Error fetching scene detection:', err);
        if (!cancelled) {
          const didTimeOut = err?.name === 'AbortError' || err === 'timeout' || err?.message === 'timeout';
          setSceneDetection(inlineSceneDetection || null);
          setLoading(false);
          setStatus(didTimeOut ? 'timeout' : 'error');
          setError(err instanceof Error ? err : new Error(String(err)));
          setTimedOut(didTimeOut);
          console.warn('[clips] scene detection fetch failed', {
            url: sceneDetectionUrl,
            durationMs: Math.round(performance.now() - startedAt),
            timedOut: didTimeOut,
            message: err instanceof Error ? err.message : String(err),
          });
        }
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
      });

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [sceneDetectionUrl, inlineSceneDetection]);

  return { data: sceneDetection, loading, status, error, timedOut };
}
